import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { LoadingState } from '@v2/design-system';
import {
    PiDownloadSimpleBold,
    PiFileTextBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type {
    CommissionGoal,
    CommissionMonthlyNf,
    SellerCommissionProfile,
    SellerHistoryMonthDetail,
} from '@v2/pages/commissioning/types';
import SellerBucketGoalsCard from './SellerBucketGoalsCard';
import SellerBucketNudge from './SellerBucketNudge';
import SellerDefaultedInstallments from './SellerDefaultedInstallments';
import SellerFocusBuckets from './SellerFocusBuckets';
import SellerForecastStrip from './SellerForecastStrip';
import SellerGeneratedCommissions from './SellerGeneratedCommissions';
import SellerHomeKpis from './SellerHomeKpis';
import { COMMISSION_HOME_MONTH_QUERY } from '@v2/hooks/commissionLiveQuery';
import SellerMonthSummaryCard from './SellerMonthSummaryCard';
import SellerNextReceipts from './SellerNextReceipts';
import SellerNfPendencies from './SellerNfPendencies';
import {
    currentMonthKey,
    monthLabel,
    monthLabelCap,
    monthOptions,
    shiftMonthKey,
} from './homeFormat';

const AVATAR_FALLBACK = '/img/favicon.png';

function initials(name: string) {
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

type Props = {
    profile: SellerCommissionProfile | undefined;
    isSelf: boolean;
    userId?: string;
    isLoading?: boolean;
    isError?: boolean;
    backToRecords?: boolean;
};

export default function SellerCommissionHomeSimple({
    profile,
    isSelf,
    userId,
    isLoading,
    isError,
    backToRecords = false,
}: Props) {
    const { t, locale } = useI18n();
    const dateLocale = locale === 'pt_BR' ? 'pt-BR' : locale === 'zh_CN' ? 'zh-CN' : locale === 'es' ? 'es' : 'en-US';

    const thisMonth = currentMonthKey();
    const [selectedMonth, setSelectedMonth] = useState(thisMonth);
    const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [showFullForecast, setShowFullForecast] = useState(false);

    const historyBase = userId
        ? `/commissioning/sellers/${userId}/history`
        : '/my-commission/history';

    const nextMonthKey = shiftMonthKey(thisMonth, 1);
    const s = profile?.summary;
    const forecast = profile?.installment_pipeline?.forecast;
    const pipeline = profile?.installment_pipeline;

    const monthChoices = useMemo(() => monthOptions(thisMonth, 18), [thisMonth]);

    const { data: monthDetailPayload, isLoading: monthLoading } = useQuery({
        queryKey: ['commissioning', 'seller-month', userId ?? 'self', selectedMonth],
        queryFn: () => {
            const url = userId
                ? `/commissioning/sellers/${userId}/profile/months/${selectedMonth}`
                : `/commissioning/my-seller-profile/months/${selectedMonth}`;
            return api.get(url).then((r) => r.data as { data: SellerHistoryMonthDetail });
        },
        ...COMMISSION_HOME_MONTH_QUERY,
    });

    const { data: goalsPayload, isLoading: goalsLoading } = useQuery({
        queryKey: ['commissioning', 'goals', selectedMonth, userId ?? 'self'],
        queryFn: () => (userId
            ? api.get('/commissioning/goals', { params: { period: selectedMonth, user_id: userId } })
            : api.get('/commissioning/goals/my-performance', { params: { period: selectedMonth } })
        ).then((r) => r.data as { data: CommissionGoal | CommissionGoal[] | null }),
        enabled: Boolean(profile),
        ...COMMISSION_HOME_MONTH_QUERY,
    });

    const { data: monthlyNfsPayload } = useQuery({
        queryKey: ['commissioning', 'monthly-nfs', 'home', userId ?? 'self', selectedMonth],
        queryFn: () => api.get('/commissioning/monthly-nfs', {
            params: {
                status: 'awaiting_nf',
                per_page: 50,
                ...(userId ? { user_id: Number(userId) } : {}),
                ...(isSelf ? { ensure: 1 } : {}),
            },
        }).then((r) => r.data as { data: CommissionMonthlyNf[] }),
        enabled: Boolean(isSelf || userId),
        ...COMMISSION_HOME_MONTH_QUERY,
    });

    const goal = useMemo(() => {
        const raw = goalsPayload?.data;
        if (!raw) return null;
        if (Array.isArray(raw)) {
            return raw.find((g) => String(g.user_id) === String(userId)) ?? raw[0] ?? null;
        }
        return raw;
    }, [goalsPayload?.data, userId]);

    const monthDetail = monthDetailPayload?.data;

    const received = monthDetail?.paid_total
        ?? (selectedMonth === thisMonth ? (s?.paid_mtd ?? 0) : 0);

    const selectedForecast = forecast?.months?.find((m) => m.month === selectedMonth);
    const toReceive = selectedMonth >= thisMonth
        ? (selectedForecast?.amount ?? 0)
        : 0;
    const toReceiveHint = toReceive > 0
        ? t('v2.my_commission.kpi_to_receive_hint')
        : t('v2.my_commission.kpi_to_receive_empty');

    const nextForecast = forecast?.months?.find((m) => m.month === nextMonthKey);
    const futureTotal = forecast?.horizon_total
        ?? pipeline?.commission_pending
        ?? s?.total_pipeline
        ?? 0;

    const commissionGenerated = monthDetail?.paid_total ?? 0;
    const salesConsidered = useMemo(() => {
        const buckets = goal?.performance?.buckets ?? [];
        const fc = buckets.filter((b) => b.bucket === 'force' || b.bucket === 'cardio');
        if (fc.length > 0) {
            return fc.reduce((sum, b) => sum + b.achieved, 0);
        }
        return goal?.performance?.global_achieved ?? 0;
    }, [goal]);

    const generatedRecords = useMemo(() => {
        return [...(monthDetail?.paid_items ?? [])]
            .sort((a, b) => b.final_amount - a.final_amount);
    }, [monthDetail?.paid_items]);

    const avgRate = useMemo(() => {
        const sample = generatedRecords.filter((r) => Number(r.deal_amount) > 0);
        if (sample.length === 0) return null;
        const rates = sample.map((r) => r.final_amount / Number(r.deal_amount));
        return rates.reduce((a, b) => a + b, 0) / rates.length;
    }, [generatedRecords]);

    const nudgeBucket = useMemo(() => {
        const buckets = [...(goal?.performance?.buckets ?? [])]
            .filter((b) => b.bucket !== 'other' && b.pct < 1)
            .sort((a, b) => a.pct - b.pct);
        return buckets[0] ?? null;
    }, [goal]);

    const handleExport = async (format: 'pdf' | 'xlsx') => {
        if (!isSelf || exporting) return;
        setExporting(format);
        setExportOpen(false);
        try {
            const response = await api.post('/commissioning/seller-period-report', {
                period_mode: 'month',
                month: selectedMonth,
                scope: 'installments',
                format,
            }, { validateStatus: (st) => st < 500 });

            if (response.status >= 400) {
                toast.error(
                    (response.data as { message?: string })?.message
                        || t('v2.my_commission.extract_error'),
                );
                return;
            }

            toast.success(
                (response.data as { message?: string })?.message
                    || t('v2.my_commission.extract_queued'),
            );
        } catch {
            toast.error(t('v2.my_commission.extract_error'));
        } finally {
            setExporting(null);
        }
    };

    if (isLoading) {
        return <LoadingState variant="dashboard" />;
    }

    if (isError || !profile || !s) {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                {t('v2.my_commission.load_error')}
            </div>
        );
    }

    return (
        <div className="w-full min-h-screen space-y-6 pb-10">
            {backToRecords && (
                <div className="flex items-center gap-2">
                    <Link
                        to="/commissioning/records"
                        className="text-sm font-medium text-orange-700 hover:underline dark:text-orange-400"
                    >
                        ← {t('v2.my_commission.back_records')}
                    </Link>
                </div>
            )}

            <header className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    {profile.user.avatar_url ? (
                        <img
                            src={profile.user.avatar_url}
                            alt={profile.user.name}
                            className="h-14 w-14 rounded-2xl object-cover ring-2 ring-white shadow dark:ring-zinc-800"
                            onError={(e) => { (e.target as HTMLImageElement).src = AVATAR_FALLBACK; }}
                        />
                    ) : (
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-lg font-bold text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                            {initials(profile.user.name)}
                        </span>
                    )}
                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-400">
                            {t('v2.my_commission.title')}
                        </p>
                        <h1 className="truncate text-2xl font-black text-gray-900 dark:text-white">
                            {profile.user.name}
                        </h1>
                        <label className="mt-1 inline-flex items-center gap-2 text-sm text-gray-600 dark:text-zinc-300">
                            <span className="sr-only">{t('v2.my_commission.month_picker')}</span>
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-medium capitalize text-gray-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                            >
                                {monthChoices.map((ym) => (
                                    <option key={ym} value={ym}>
                                        {monthLabelCap(ym, dateLocale)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Link
                        to={`${historyBase}/${selectedMonth}`}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-orange-300 hover:text-orange-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                        <PiFileTextBold className="size-4" />
                        {t('v2.my_commission.btn_month_summary')}
                    </Link>
                    {isSelf && (
                        <div className="relative">
                            <button
                                type="button"
                                disabled={exporting !== null}
                                onClick={() => setExportOpen((o) => !o)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-orange-300 hover:text-orange-700 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <PiDownloadSimpleBold className="size-4" />
                                {exporting
                                    ? t('v2.my_commission.btn_extracting')
                                    : t('v2.my_commission.btn_extract_report')}
                            </button>
                            {exportOpen && (
                                <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
                                    <button
                                        type="button"
                                        className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50 dark:hover:bg-zinc-800"
                                        onClick={() => handleExport('pdf')}
                                    >
                                        PDF
                                    </button>
                                    <button
                                        type="button"
                                        className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50 dark:hover:bg-zinc-800"
                                        onClick={() => handleExport('xlsx')}
                                    >
                                        Excel
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <SellerHomeKpis
                selectedMonth={selectedMonth}
                received={received}
                toReceive={toReceive}
                toReceiveHint={toReceiveHint}
                nextMonthAmount={nextForecast?.amount ?? 0}
                nextMonthCount={nextForecast?.installments_count ?? 0}
                nextMonthLabel={monthLabel(nextMonthKey, dateLocale)}
                futureTotal={futureTotal}
            />

            <SellerNfPendencies
                selectedMonth={selectedMonth}
                nfs={monthlyNfsPayload?.data ?? []}
                isSelf={isSelf}
                userId={userId}
            />

            <SellerDefaultedInstallments data={profile?.defaulted_installments} />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
                <div className="space-y-4 xl:col-span-3">
                    <SellerMonthSummaryCard
                        selectedMonth={selectedMonth}
                        goal={goal}
                        commissionGenerated={commissionGenerated}
                        salesConsidered={salesConsidered}
                        isLoading={goalsLoading || monthLoading}
                    />
                    <SellerNextReceipts
                        forecast={forecast}
                        historyBase={historyBase}
                        onShowFull={() => setShowFullForecast((v) => !v)}
                    />
                </div>
                <div className="space-y-4 xl:col-span-2">
                    <SellerBucketGoalsCard userId={userId} period={selectedMonth} />
                    <SellerFocusBuckets
                        goal={goal}
                        isLoading={goalsLoading}
                        avgRate={avgRate}
                    />
                </div>
            </div>

            {showFullForecast && (
                <SellerForecastStrip forecast={forecast} />
            )}

            <SellerGeneratedCommissions
                records={generatedRecords}
                historyMonthTo={`${historyBase}/${selectedMonth}`}
                isLoading={monthLoading && !monthDetail}
            />

            <SellerBucketNudge incomplete={nudgeBucket} avgRate={avgRate} />
        </div>
    );
}
